import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Calendar as CalendarIcon, Clock, Users, FileText,
  Loader2, Plus, CheckCircle2, XCircle, AlertCircle, Banknote, Upload, X,
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'Awaiting approval', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved_awaiting_payment: { label: 'Approved — upload EFT proof', color: 'bg-sky-100 text-sky-800', icon: Banknote },
  proof_uploaded: { label: 'Proof submitted — awaiting verification', color: 'bg-indigo-100 text-indigo-800', icon: FileText },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-rose-100 text-rose-700', icon: XCircle },
  payment_rejected: { label: 'Proof rejected — re-upload', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: X },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: 'bg-slate-100 text-slate-700', icon: AlertCircle };
  const Icon = s.icon;
  return (
    <Badge className={`${s.color} gap-1 font-medium`} data-testid={`status-${status}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </Badge>
  );
}

export default function ResidentVenueBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hoaId = params.get('building') || user?.hoa_id;

  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const [form, setForm] = useState({
    venue_id: '', start_time: '', end_time: '', purpose: '',
    expected_guests: '', special_requirements: '', contact_phone: '',
  });
  const [proofForm, setProofForm] = useState({
    file: null, payment_reference: '', paid_amount: '', paid_at: '',
  });

  const token = () => localStorage.getItem('session_token');
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token()}`,
  });

  const fetchAll = useCallback(async () => {
    if (!hoaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [vRes, bRes] = await Promise.all([
        fetch(`${API_URL}/api/venue-bookings/venues/${hoaId}`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API_URL}/api/venue-bookings/my-bookings`, { headers: authHeaders(), credentials: 'include' }),
      ]);
      if (vRes.ok) {
        const data = await vRes.json();
        setVenues(data.venues || []);
      } else if (vRes.status === 403) {
        toast.error('You must be an approved resident to view venues for this building.');
      }
      if (bRes.ok) {
        const data = await bRes.json();
        setBookings((data.bookings || []).filter((b) => !hoaId || b.hoa_id === hoaId));
      }
    } catch (e) {
      toast.error('Failed to load venue bookings');
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.venue_id || !form.start_time || !form.end_time || !form.purpose) {
      toast.error('Please fill venue, start, end and purpose');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/book`, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          hoa_id: hoaId,
          venue_id: form.venue_id,
          start_time: new Date(form.start_time).toISOString(),
          end_time: new Date(form.end_time).toISOString(),
          purpose: form.purpose,
          expected_guests: form.expected_guests ? Number(form.expected_guests) : undefined,
          special_requirements: form.special_requirements || undefined,
          contact_phone: form.contact_phone || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      toast.success('Booking submitted! Awaiting approval.');
      setBookOpen(false);
      setForm({ venue_id: '', start_time: '', end_time: '', purpose: '', expected_guests: '', special_requirements: '', contact_phone: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${bookingId}/cancel`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cancel failed');
      toast.success('Booking cancelled');
      setDetailOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const openBookingDetail = async (b) => {
    setSelected(null);
    setDetailOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/venue-bookings/${b.booking_id}`, {
        headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) setSelected(await res.json());
      else setSelected(b);
    } catch { setSelected(b); }
  };

  const submitProof = async (e) => {
    e.preventDefault();
    if (!proofForm.file) { toast.error('Select a file (image or PDF)'); return; }
    if (proofForm.file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', proofForm.file);
      if (proofForm.payment_reference) fd.append('payment_reference', proofForm.payment_reference);
      if (proofForm.paid_amount) fd.append('paid_amount', proofForm.paid_amount);
      if (proofForm.paid_at) fd.append('paid_at', new Date(proofForm.paid_at).toISOString());
      const res = await fetch(`${API_URL}/api/venue-bookings/${selected.booking_id}/upload-proof`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success('Proof uploaded — the management team will verify.');
      setUploadOpen(false);
      setProofForm({ file: null, payment_reference: '', paid_amount: '', paid_at: '' });
      fetchAll();
      openBookingDetail(selected);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }
  if (!hoaId) {
    return (
      <div className="min-h-screen p-8 max-w-2xl mx-auto">
        <Card><CardContent className="p-6">No building selected. Please open this page from a building workspace.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Venue Bookings</h1>
              <p className="text-sm text-slate-500">Book a venue, then pay via EFT and upload your proof.</p>
            </div>
          </div>
          <Button onClick={() => setBookOpen(true)} data-testid="new-booking-btn" disabled={venues.length === 0}>
            <Plus className="w-4 h-4 mr-1" /> New Booking
          </Button>
        </div>

        <Tabs defaultValue="bookings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="tab-bookings">My Bookings</TabsTrigger>
            <TabsTrigger value="venues" data-testid="tab-venues">Available Venues</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="space-y-3">
            {bookings.length === 0 && (
              <Card><CardContent className="p-12 text-center text-slate-500">
                <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                No bookings yet. Tap &ldquo;New Booking&rdquo; to reserve a venue.
              </CardContent></Card>
            )}
            {bookings.map((b) => (
              <Card key={b.booking_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openBookingDetail(b)} data-testid={`booking-card-${b.booking_id}`}>
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{b.venue_name}</span>
                      <StatusBadge status={b.status} />
                    </div>
                    <div className="text-sm text-slate-500 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" />{format(new Date(b.start_time), 'PPp')}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />→ {format(new Date(b.end_time), 'PPp')}</span>
                    </div>
                    {b.quoted_amount != null && (
                      <div className="text-sm mt-1">Invoice <span className="font-mono">{b.invoice_number}</span> — <span className="font-semibold">R{Number(b.quoted_amount).toFixed(2)}</span></div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="venues" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {venues.length === 0 && <Card className="sm:col-span-2 lg:col-span-3"><CardContent className="p-8 text-center text-slate-500">No venues defined for this building yet.</CardContent></Card>}
            {venues.map((v) => (
              <Card key={v.venue_id} data-testid={`venue-card-${v.venue_id}`}>
                <CardHeader>
                  <CardTitle className="text-base">{v.name}</CardTitle>
                  {v.capacity ? <CardDescription className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />Capacity {v.capacity}</CardDescription> : null}
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-slate-600">
                  {v.description && <p>{v.description}</p>}
                  {v.hourly_rate != null && <p>Hourly rate: <span className="font-semibold">R{Number(v.hourly_rate).toFixed(2)}</span></p>}
                  {v.daily_rate != null && <p>Daily rate: <span className="font-semibold">R{Number(v.daily_rate).toFixed(2)}</span></p>}
                  {v.requires_deposit && v.deposit_amount != null && <p className="text-amber-700">Deposit: R{Number(v.deposit_amount).toFixed(2)}</p>}
                  {v.booking_rules && <p className="text-xs text-slate-500 whitespace-pre-line">{v.booking_rules}</p>}
                  <Button size="sm" className="w-full" onClick={() => { setForm((f) => ({ ...f, venue_id: v.venue_id })); setBookOpen(true); }} data-testid={`book-venue-${v.venue_id}`}>Book this venue</Button>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* New booking dialog */}
      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-lg" data-testid="new-booking-dialog">
          <DialogHeader>
            <DialogTitle>New Venue Booking</DialogTitle>
            <DialogDescription>Submit your request. A managing agent will approve it and send you payment details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-3">
            <div>
              <Label>Venue *</Label>
              <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
                <SelectTrigger data-testid="venue-select"><SelectValue placeholder="Pick a venue" /></SelectTrigger>
                <SelectContent>
                  {venues.map((v) => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start *</Label>
                <Input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="start-input" />
              </div>
              <div>
                <Label>End *</Label>
                <Input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="end-input" />
              </div>
            </div>
            <div>
              <Label>Purpose *</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Birthday party, AGM, etc." data-testid="purpose-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Expected guests</Label>
                <Input type="number" min={0} value={form.expected_guests} onChange={(e) => setForm({ ...form, expected_guests: e.target.value })} data-testid="guests-input" />
              </div>
              <div>
                <Label>Contact phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="phone-input" />
              </div>
            </div>
            <div>
              <Label>Special requirements</Label>
              <Textarea rows={3} value={form.special_requirements} onChange={(e) => setForm({ ...form, special_requirements: e.target.value })} data-testid="requirements-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBookOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-booking-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl" data-testid="booking-detail-dialog">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{selected.venue_name} <StatusBadge status={selected.status} /></DialogTitle>
                <DialogDescription>Booking #{selected.booking_id.slice(-8)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-slate-500">Start:</span> {format(new Date(selected.start_time), 'PPp')}</div>
                  <div><span className="text-slate-500">End:</span> {format(new Date(selected.end_time), 'PPp')}</div>
                  <div><span className="text-slate-500">Purpose:</span> {selected.purpose}</div>
                  {selected.expected_guests ? <div><span className="text-slate-500">Guests:</span> {selected.expected_guests}</div> : null}
                </div>
                {selected.special_requirements && (
                  <div><span className="text-slate-500">Requirements:</span> {selected.special_requirements}</div>
                )}
                {selected.status === 'rejected' && selected.rejection_reason && (
                  <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800">Rejected: {selected.rejection_reason}</div>
                )}
                {['approved_awaiting_payment', 'proof_uploaded', 'payment_rejected', 'confirmed'].includes(selected.status) && (
                  <div className="rounded-md border bg-slate-50 p-4 space-y-1">
                    <div className="font-medium">Invoice</div>
                    <div className="text-sm">No: <span className="font-mono">{selected.invoice_number}</span></div>
                    <div className="text-sm">Amount: <span className="font-semibold">R{Number(selected.quoted_amount || 0).toFixed(2)}</span></div>
                    {selected.payment_due_date && <div className="text-sm">Due: {format(new Date(selected.payment_due_date), 'PP')}</div>}
                    {selected.invoice_notes && <div className="text-sm whitespace-pre-line">{selected.invoice_notes}</div>}
                  </div>
                )}
                {selected.payment_profile && ['approved_awaiting_payment', 'proof_uploaded', 'payment_rejected'].includes(selected.status) && (
                  <div className="rounded-md border bg-emerald-50 border-emerald-200 p-4 space-y-1">
                    <div className="font-medium flex items-center gap-1"><Banknote className="w-4 h-4" /> EFT Bank Details</div>
                    <div className="text-sm">Bank: {selected.payment_profile.bank_name}</div>
                    <div className="text-sm">Account name: {selected.payment_profile.account_name}</div>
                    <div className="text-sm">Account no: <span className="font-mono">{selected.payment_profile.account_number}</span></div>
                    {selected.payment_profile.branch_code && <div className="text-sm">Branch code: {selected.payment_profile.branch_code}</div>}
                    {selected.payment_profile.swift_code && <div className="text-sm">SWIFT: {selected.payment_profile.swift_code}</div>}
                    <div className="text-sm">Use reference: <span className="font-mono">{selected.invoice_number}</span></div>
                    {selected.payment_profile.payment_instructions && <div className="text-xs text-slate-600 whitespace-pre-line mt-1">{selected.payment_profile.payment_instructions}</div>}
                  </div>
                )}
                {selected.payment_proof_url && (
                  <div className="rounded-md border p-3 text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <a href={`${API_URL}${selected.payment_proof_url}`} target="_blank" rel="noreferrer" className="underline">View uploaded proof</a>
                    <span className="text-xs text-slate-500">{selected.payment_proof_original_name}</span>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2">
                {selected.status === 'pending' && (
                  <Button variant="destructive" onClick={() => cancelBooking(selected.booking_id)} data-testid="cancel-booking-btn">Cancel booking</Button>
                )}
                {['approved_awaiting_payment', 'payment_rejected'].includes(selected.status) && (
                  <Button onClick={() => setUploadOpen(true)} data-testid="upload-proof-btn"><Upload className="w-4 h-4 mr-1" />Upload proof</Button>
                )}
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </>
          ) : <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
        </DialogContent>
      </Dialog>

      {/* Upload proof dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="upload-proof-dialog">
          <DialogHeader>
            <DialogTitle>Upload Proof of Payment</DialogTitle>
            <DialogDescription>Image or PDF, max 8 MB.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitProof} className="space-y-3">
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
                onChange={(e) => setProofForm({ ...proofForm, file: e.target.files?.[0] || null })}
                data-testid="proof-file-input"
              />
            </div>
            <div>
              <Label>Payment reference</Label>
              <Input value={proofForm.payment_reference} onChange={(e) => setProofForm({ ...proofForm, payment_reference: e.target.value })} placeholder="EFT reference" data-testid="payment-reference-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount paid</Label>
                <Input type="number" step="0.01" value={proofForm.paid_amount} onChange={(e) => setProofForm({ ...proofForm, paid_amount: e.target.value })} data-testid="paid-amount-input" />
              </div>
              <div>
                <Label>Paid at</Label>
                <Input type="datetime-local" value={proofForm.paid_at} onChange={(e) => setProofForm({ ...proofForm, paid_at: e.target.value })} data-testid="paid-at-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-proof-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
